import React, { useState } from 'react';
import Hero from '../../components/Hero/Hero';
import Pathfinder from '../../components/Pathfinder/Pathfinder';
import Metadata from '../../components/Metadata/Metadata';
import Section from '../../components/Section/Section';
import Card from '../../components/Card/Card';
import GlobalCallout from '../../components/Global/CalloutDefault';
import { Box, Typography } from '@mui/material';
import Wallpaper from '../../components/Wallpaper/Wallpaper';
import Accordion from '../../components/Accordion/Accordion';
import Button from '../../components/Button/Button';
import Image from '../../components/Image/Image';
import ContentSplit from '../../components/ContentSplit/ContentSplit';
import ListItem from '../../components/ListItem/ListItem';
import { Link } from 'react-router-dom';

const PageContent: React.FC = () => {
  const [expandAll, setExpandAll] = useState(false);

  const toggleAll = () => {
    setExpandAll(!expandAll);
  };

  return (
    <Box>
      <Metadata
        title="Education & Development | Advanced AI Training at General Genomics"
        description="Explore General Genomics’ tailored training programs in AI and data solutions. Empower professionals and organizations to optimize workflows and integrate cutting-edge technologies."
      />
      <Hero
        helper="Innovation"
        headline="Advancing expertise with AI and data-focused workforce development"
      />

      <Section theme="" className="cards-large-icons">
        <h2 className="action-on-view action-fade-in">
          Empowering Teams with AI
        </h2>
        <p className="action-on-view action-fade-in">
          Unlock the future of workforce development with General Genomics&apos;
          tailored training programs. Our specialized courses in AI/ML
          integration and data solutions are designed to help your organization
          optimize workflows and stay ahead in a competitive landscape. With a
          proven track record of success across government and commercial
          sectors, we deliver impactful education initiatives that drive
          meaningful transformation. Discover how our expertise in cutting-edge
          technologies can empower your team and maximize your potential.
          Contact us today to explore how our programs can address your unique
          challenges and objectives.
        </p>
        <Image
          path="/images/library/office-group-meeting-2.webp"
          size="wide"
          className="action-on-view action-fade-in"
          alt="This is a wide image."
        />
      </Section>

      <ContentSplit
        image="/images/library/military-meeting-3.webp"
        position="right"
      >
        <p className="helper action-on-view action-fade-in">Explore More</p>
        <h2 className="action-on-view action-fade-in">
          Tailored Training That Transforms
        </h2>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Custom AI/ML Workshops:</strong> Hands-on courses designed
            to integrate AI into workflows effectively.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Workforce Development Strategies:</strong> Programs to
            prepare teams for adopting cutting-edge technologies.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Sector-Specific Solutions:</strong> Tailored training
            initiatives proven successful in government and commercial sectors.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Success Stories & Testimonials:</strong> Real-world examples
            demonstrating measurable impact and transformation.
          </p>
        </ListItem>
        <ListItem
          icon="check"
          iconClass="highlighted-icon"
          className="custom-list-item"
        >
          <p>
            <strong>Interactive Program Outlines:</strong> Comprehensive,
            customizable modules for diverse organizational needs.
          </p>
        </ListItem>
      </ContentSplit>

      <Section theme="dark" className="section-cross-linking">
        <h2 className="action-on-view action-fade-in">
          Explore More About Our Genomics Services
        </h2>
        <p className="action-on-view action-fade-in">
          Discover additional resources and insights about General
          Genomics&apos; offerings, from training capabilities to academic
          applications and custom demos.
        </p>
        <div className="card-group">
          <Card
            icon="school"
            headline="Our Capabilities"
            subhead="Learn how our advanced training enhances our genomics services."
            btnURL="/our-approach/our-capabilities"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
          <Card
            icon="library_books"
            headline="Academic Research"
            subhead="Explore how we apply genomics in education and workforce development."
            btnURL="/industries/commercial/researchers"
            btnLabel="Learn more"
            className="action-on-view action-fade-in"
          />
        </div>
      </Section>

      <Wallpaper asset="../images/library/classroom-group.webp"></Wallpaper>

      <Section>
        <Typography variant="h2" className="action-on-view action-fade-in">
          Frequently asked questions about General Genomics&apos; training
          programs
        </Typography>
        <Typography className="action-on-view action-fade-in">
          Learn about our tailored AI, data solutions, and workforce development
          training for professionals and organizations across sectors.
        </Typography>
        <Box className="accordion-wrapper action-on-view action-fade-in">
          <Button
            className="accordion-button"
            type="outlined"
            onClick={toggleAll}
            icon={expandAll ? 'expand_less' : 'expand_more'}
            label={expandAll ? 'Collapse All' : 'Expand All'}
          />
          <Accordion
            isOpen={expandAll}
            headline="What types of training programs does General Genomics offer?"
          >
            General Genomics provides advanced training in AI, data solutions,
            and workforce development, with programs focused on AI/ML
            integration and workflow optimization across various sectors.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Are the training programs customizable for specific organizational needs?"
          >
            Yes, we offer tailored workshops and courses designed to meet the
            unique needs of each client, ensuring the most relevant and
            effective training for their team.
          </Accordion>
          <Accordion
            isOpen={expandAll}
            headline="Can General Genomics’ training programs be applied in both government and commercial sectors?"
          >
            Absolutely. We have successfully implemented training initiatives
            across both <Link to="/industries/government">government</Link> and{' '}
            <Link to="/industries/commercial">commercial</Link> sectors,
            optimizing workflows and enhancing AI integration in diverse
            environments.
          </Accordion>

          <Accordion
            isOpen={expandAll}
            headline="What outcomes can I expect from General Genomics' education programs?"
          >
            Our programs empower organizations by improving AI integration,
            optimizing workflows, and enhancing overall productivity, as
            demonstrated by successful implementations in various sectors.
          </Accordion>
        </Box>
      </Section>

      <Section theme="dark" bgName="lines" className="section-call-to-action">
        <Typography variant="h2" className="action-on-view action-fade-in">
          Why partnering with General Genomics transforms bioinformatics
          research and innovation
        </Typography>
        <Box className="card-group">
          <Card
            icon="check"
            headline="Advanced Solutions in Analytics"
            subhead="General Genomics combines analytics and innovation with cutting-edge algorithms, large language models, machine learning, and predictive analytics to transform synthetic systems and complex data insights."
            className="action-on-view action-fade-in"
          />
          <Card
            icon="check"
            headline="Precision Insights"
            subhead="Harness predictive modeling and genomic data analysis to drive accurate, actionable insights tailored to research and healthcare objectives."
            className="action-on-view action-fade-in action-delay-250"
          />
          <Card
            icon="check"
            headline="Collaborative Expertise"
            subhead="Partner with a team experienced in supporting government initiatives to advance bioinformatics research and public health strategies."
            className="action-on-view action-fade-in action-delay-500"
          />
        </Box>
      </Section>

      <GlobalCallout />

      <Pathfinder />
    </Box>
  );
};

export default PageContent;
